import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FiUpload, FiImage, FiX, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { uploadAPI } from '../services/api';

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UploadArea = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isDragOver'].includes(prop),
})`
  border: 2px dashed ${props => props.isDragOver ? '#3b82f6' : '#d1d5db'};
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.isDragOver ? '#eff6ff' : '#f9fafb'};
  
  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
`;

const UploadIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  color: #6b7280;
  font-size: 32px;
`;

const UploadText = styled.div`
  color: #374151;
  font-size: 14px;
  margin-bottom: 8px;
`;

const UploadSubtext = styled.div`
  color: #6b7280;
  font-size: 12px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const PreviewContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 8px;
  margin-top: 12px;
`;

const PreviewImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const PreviewInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PreviewName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PreviewSize = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'success' && `
    background: #10b981;
    color: white;
    
    &:hover {
      background: #059669;
    }
  `}
  
  ${props => props.variant === 'danger' && `
    background: #ef4444;
    color: white;
    
    &:hover {
      background: #dc2626;
    }
  `}
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

function ImageUpload({ value, onChange, onError, accept = 'image/*', maxSize = 5 * 1024 * 1024 }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle file selection
  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      if (onError) onError('Invalid file type');
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      toast.error(`File size must be less than ${formatFileSize(maxSize)}`);
      if (onError) onError('File too large');
      return;
    }

    setIsUploading(true);

    try {
      const response = await uploadAPI.uploadLogo(file);
      const uploadedFile = response.data.data;
      
      // Set preview
      setPreview({
        name: uploadedFile.originalName,
        size: uploadedFile.size,
        url: uploadAPI.getLogoUrl(uploadedFile.filename)
      });

      // Call onChange with the full URL
      const fullUrl = uploadAPI.getLogoUrl(uploadedFile.filename);
      onChange(fullUrl);
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
      if (onError) onError('Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle click to upload
  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle remove image
  const handleRemove = () => {
    setPreview(null);
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <UploadContainer>
      <UploadArea
        isDragOver={isDragOver}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <UploadIcon>
          {isUploading ? (
            <LoadingSpinner />
          ) : preview ? (
            <FiCheck style={{ color: '#10b981' }} />
          ) : (
            <FiUpload />
          )}
        </UploadIcon>
        
        {isUploading ? (
          <UploadText>Uploading...</UploadText>
        ) : preview ? (
          <UploadText>Image uploaded successfully</UploadText>
        ) : (
          <>
            <UploadText>Click to upload or drag and drop</UploadText>
            <UploadSubtext>
              PNG, JPG, GIF, WebP, SVG up to {formatFileSize(maxSize)}
            </UploadSubtext>
          </>
        )}
      </UploadArea>

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInputChange}
      />

      {preview && (
        <PreviewContainer>
          <PreviewImage src={preview.url} alt="Preview" />
          <PreviewInfo>
            <PreviewName>{preview.name}</PreviewName>
            <PreviewSize>{formatFileSize(preview.size)}</PreviewSize>
          </PreviewInfo>
          <ActionButtons>
            <ActionButton variant="danger" onClick={handleRemove} title="Remove image">
              <FiX size={16} />
            </ActionButton>
          </ActionButtons>
        </PreviewContainer>
      )}
    </UploadContainer>
  );
}

export default ImageUpload;
